import Modal from '../../DesignSystem/Modal';
import Navbar from '../../DesignSystem/Navbar';
import SearchBar from '../../DesignSystem/SearchBar';
import s from './SavedAddresses.module.scss';
import { ReactComponent as LocatorIcon } from '../../Icons/Locator.svg';
import { ReactComponent as ArrowRight } from '../../Icons/AngleRight.svg';
import AddressCard from '../Cards/AddressCard';
import AddNewAddress from '../AddNewAddress';
import { getSavedAddresses, removeAddress } from '../../../../../services';
import { useEffect, useState } from 'react';
import { getAddressFromLatLng } from '../../services';
import { useSelector } from 'react-redux';
import useSessionStorage from '../../hooks/useStorage';
import { useRef, useMemo } from 'react';
import MapModal from '../MapModal';
import apiClient from '../../../../../services/apiClient';

const SavedAddressess = ({
  isOpen,
  onClose,
  hideSkipButton = false,
  onAddressSelect,
  cartId,
  isPhysioHomeVisit = false,
  closeOnSelect = true,
}) => {
  console.log('savedAddress', hideSkipButton);
  const { getFromStorage, removeFromStorage } = useSessionStorage();
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showAddNewAddressModal, setShowAddAddressModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({
    lat: null,
    long: null,
    address: '',
    loading: false,
    formattedAddress: null,
  });

  const [addressesLoading, setAddressesLoading] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [newAddress, setNewAddress] = useState(null);
  const [navigatorPosition, setNavigatorPosition] = useState({
    lat: null,
    lng: null,
  });
  const [hasLocationPermission, setHasLocationPermission] = useState(false);

  const [searchText, setSearchText] = useState('');

  const authToken = useSelector((state) => state?.user?.authToken);

  /* -------------------------------
     Fetch saved addresses
  -------------------------------- */
  useEffect(() => {
    if (!isOpen) return;

    setAddressesLoading(true);
    if (isPhysioHomeVisit) {
      apiClient
        .get(`/addresses?centerId=${cartId}`)
        .then((res) => {
          setSavedAddresses(res?.data || []);
        })
        .finally(() => {
          setAddressesLoading(false);
        });
      return;
    }

    getSavedAddresses(authToken, cartId)
      .then((res) => {
        setSavedAddresses(res?.addresses || []);
      })
      .finally(() => {
        setAddressesLoading(false);
      });
  }, [isOpen, authToken, cartId]);

  /* -------------------------------
     Get current location (lat/lng)
  -------------------------------- */
  useEffect(() => {
    if (!isOpen) return;

    if (!navigator.geolocation) return;

    setCurrentLocation((prev) => ({ ...prev, loading: true }));

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setHasLocationPermission(true);
        const lat = position.coords.latitude;
        const long = position.coords.longitude;
        setNavigatorPosition({ lat, long });
        let tempSavedAddress = getFromStorage('selected_address');
        if (tempSavedAddress && !tempSavedAddress?.addressId) {
          setCurrentLocation({
            lat: tempSavedAddress?.lat,
            long: tempSavedAddress?.long,
            formattedAddress:
              tempSavedAddress?.address || tempSavedAddress?.formattedAddress,
            address:
              tempSavedAddress?.city ||
              tempSavedAddress?.address ||
              'Unable to fetch address',
          });
          return;
        }
        try {
          const res = await getAddressFromLatLng(lat, long, authToken);
          setCurrentLocation({
            lat,
            long,
            formattedAddress: res?.result?.formattedAddress,
            address:
              res?.result?.city || res?.address || 'Unable to fetch address',
            loading: false,
          });
        } catch (e) {
          setHasLocationPermission(false);
          setCurrentLocation({
            lat: null,
            long: null,
            formattedAddress: null,
            address: 'Unable to fetch address',
          });
        }
      },
      () => {
        setCurrentLocation((prev) => ({
          ...prev,
          loading: false,
          address: 'Location permission denied',
        }));
      },
      { enableHighAccuracy: true }
    );
  }, [isOpen, authToken]);

  /* -------------------------------
     Refresh addresses 
  -------------------------------- */
  const refreshAddresses = () => {
    setAddressesLoading(true);

    getSavedAddresses(authToken, cartId)
      .then((res) => {
        setSavedAddresses(res?.addresses || []);
      })
      .finally(() => {
        setAddressesLoading(false);
      });
  };

  const filteredAddresses = useMemo(() => {
    if (!searchText.trim()) return savedAddresses;

    const query = searchText.toLowerCase();

    return savedAddresses.filter((address) => {
      return (
        address?.address?.toLowerCase().includes(query) ||
        address?.city?.toLowerCase().includes(query) ||
        address?.label?.toLowerCase().includes(query) ||
        address?.title?.toLowerCase().includes(query) ||
        address?.pincode?.toString().includes(query)
      );
    });
  }, [searchText, savedAddresses]);

  // live location  if the lat lng matches the navigators lat lng then show current location else selected location
  const selectedAddress = getFromStorage('selected_address');

  const isLiveLocation = useMemo(() => {
    if (!currentLocation?.lat || !currentLocation?.long) return false;
    if (!navigatorPosition?.lat || !navigatorPosition?.long) return false;

    const latDiff = Math.abs(currentLocation.lat - navigatorPosition.lat);
    const lngDiff = Math.abs(currentLocation.long - navigatorPosition.long);

    const threshold = 0.0003; // ~30 meters approx

    return latDiff < threshold && lngDiff < threshold;
  }, [currentLocation, navigatorPosition]);

  return (
    <>
      <Modal isOpen={isOpen} isFullScreen={!isPhysioHomeVisit}>
        <div className={s.root}>
          {!isPhysioHomeVisit && (
            <Navbar label="Select Address" onBack={onClose} />
          )}

          {/* ---------------------------
              Current Location
          ---------------------------- */}
          <div
            className={s.currentLocationContainer}
            onClick={() => {
              setNewAddress({
                lat: currentLocation.lat,
                lng: currentLocation.long,
                address: currentLocation.address,
              });
              setShowMapModal(true);
            }}
          >
            <LocatorIcon />

            <div className={s.content}>
              <p className={s.title}>
                {' '}
                {isLiveLocation
                  ? 'Your Current Location'
                  : 'Add as a new address'}
              </p>
              {!isLiveLocation && (
                <p className={s.leadText}>Current Location</p>
              )}
              <p className={s.address}>
                {currentLocation.loading
                  ? 'Fetching current location...'
                  : currentLocation?.formattedAddress ||
                    currentLocation?.address ||
                    'Address not available'}
              </p>
            </div>

            <ArrowRight height={12} width={12} />
          </div>
          <div className={s.separator}>
            <hr />
            <p>Saved Addresses</p>
            <hr />
          </div>

          {addressesLoading ? (
            <p className={s.emptyState}>Loading addresses...</p>
          ) : filteredAddresses?.length > 0 ? (
            <div className={s.savedAddressesContainer}>
              {filteredAddresses.map((address) => (
                <AddressCard
                  {...address}
                  isSelected={
                    address?.addressId ===
                    getFromStorage('selected_address')?.addressId
                  }
                  onEdit={() => {
                    setEditingAddress(address);
                    setShowMapModal(true);
                  }}
                  onDelete={() => {
                    removeAddress(address?.addressId, authToken).then(() => {
                      refreshAddresses();
                      const selectedAddress =
                        getFromStorage('selected_address');

                      if (selectedAddress?.addressId === address?.addressId) {
                        removeFromStorage('selected_address');
                        window?.dispatchEvent(new Event('address_selected'));
                      }
                    });
                  }}
                  onClick={() => {
                    onAddressSelect?.(address);
                    if (closeOnSelect) onClose();
                  }}
                  key={address?.addressId}
                />
              ))}
            </div>
          ) : (
            <p className={s.emptyState}>No saved addresses found</p>
          )}
        </div>
      </Modal>
      {/* Geo map will open here  */}
      {/* ------------------ Map Modal--------------- */}
      <MapModal
        show={showMapModal}
        initialLat={editingAddress?.lat || currentLocation?.lat}
        initialLng={editingAddress?.long || currentLocation?.long}
        onClose={() => {
          setEditingAddress(null);
          setShowMapModal(false);
        }}
        onConfirm={(val) => {
          if (editingAddress) {
            setEditingAddress({
              ...editingAddress,
              lat: val.lat,
              long: val.long,
              address: val.fullAddress,
              locality: val.city,
              pincode: val.pincode,
            });
          } else {
            setNewAddress(val);
          }
          setShowAddAddressModal(true);
          setShowMapModal(false);
        }}
      />
      {/* Add or edit address */}
      <AddNewAddress
        isOpen={showAddNewAddressModal}
        hideSkipButton={hideSkipButton || !newAddress}
        editingAddress={editingAddress}
        newAddress={newAddress}
        onSkipClick={(address) => {
          onAddressSelect(address);
          setShowAddAddressModal(false);
          setEditingAddress(null);
          setNewAddress(null);
          if (closeOnSelect) onClose();
        }}
        onClose={() => {
          setShowAddAddressModal(false);
          setEditingAddress(null);
          setNewAddress(null);
        }}
        onSave={() => {
          refreshAddresses();
        }}
      />
    </>
  );
};

export default SavedAddressess;
